import { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import { InsuranceDataProvider } from './contexts/InsuranceDataContext';
import { PromptProvider } from './contexts/PromptContext';
import { OfflineProvider, useOffline } from './contexts/OfflineContext';
import LoginScreen from './components/LoginScreen';
import TradesBeam from './components/TradesBeam';
import LandingPage from './components/LandingPage';
import AdminLayout from './components/admin/AdminLayout';

// Simple router based on pathname
const getRoute = () => {
  const path = window.location.pathname;
  if (path === '/' || path === '') return 'landing';
  if (path.startsWith('/trades-app')) return 'trades-app';
  if (path.startsWith('/insurance-estimator')) return 'insurance-estimator';
  if (path.startsWith('/admin')) return 'admin';
  return 'landing';
};

// Sync status indicator component
const SyncIndicator = () => {
  const { isOnline, syncStatus, pendingPhotoCount, pendingFormCount, syncError } = useOffline();

  if (syncStatus === 'synced' && isOnline) {
    return null; // Don't show anything when fully synced
  }

  return (
    <div className="fixed top-2 right-2 z-50">
      {!isOnline && (
        <div className="bg-yellow-500 text-black px-3 py-1 rounded-full text-xs font-medium mb-1">
          Offline
        </div>
      )}
      {syncStatus === 'pending' && (
        <div className="bg-yellow-500 text-black px-3 py-1 rounded-full text-xs font-medium">
          {pendingPhotoCount + pendingFormCount} pending
        </div>
      )}
      {syncStatus === 'error' && (
        <div className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-medium cursor-pointer" title={syncError}>
          Sync error
        </div>
      )}
    </div>
  );
};

// Main app content with routing
const AppContent = () => {
  const { user, loading } = useAuth();
  const [route, setRoute] = useState(getRoute);

  // Listen for browser navigation
  useEffect(() => {
    const handlePopState = () => setRoute(getRoute());
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Landing page - no auth required
  if (route === 'landing') {
    return <LandingPage />;
  }

  // All other routes require auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0A' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 mx-auto mb-4" style={{ borderColor: '#E2FF2E' }}></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginScreen />;
  }

  // Admin panel
  if (route === 'admin') {
    return (
      <DataProvider>
        <PromptProvider>
          <AdminLayout />
        </PromptProvider>
      </DataProvider>
    );
  }

  // Insurance estimator - lightweight providers (only insurance_estimates + ai_prompts)
  if (route === 'insurance-estimator') {
    return (
      <InsuranceDataProvider>
        <PromptProvider>
          <TradesBeam />
        </PromptProvider>
      </InsuranceDataProvider>
    );
  }

  // Trades app
  return (
    <DataProvider>
      <PromptProvider>
        <OfflineProvider>
          <SyncIndicator />
          <TradesBeam />
        </OfflineProvider>
      </PromptProvider>
    </DataProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
